import { useEffect } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import { Nav } from "@/components/Nav";
import { InstagramEmbed } from "@/components/InstagramEmbed";
import { fetchWorkPost } from "@/lib/api";
import { WORK_DETAIL } from "@/constants/testIds";

export default function WorkPostDetail() {
  const { shortcode } = useParams();
  const location = useLocation();
  const backHref = `/${location.state?.fromSearch || ""}`;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["work-post", shortcode],
    queryFn: () => fetchWorkPost(shortcode),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [shortcode]);

  useEffect(() => {
    const post = data?.post;
    if (!post) return;
    const prevTitle = document.title;
    document.title = `${post.brandName || "Unverified brand"} — EiPi Media`;
    const coverPath = post.posterUrlLarge || post.posterUrl;
    // OG tags must be absolute for social crawlers - resolve against the CURRENT origin at
    // render time (works correctly on preview and eipimedia.com alike), never a hardcoded host.
    const ogImage = coverPath ? new URL(coverPath, window.location.origin).toString() : null;
    let meta = document.querySelector('meta[property="og:image"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("property", "og:image");
      document.head.appendChild(meta);
    }
    const prevContent = meta.getAttribute("content");
    if (ogImage) meta.setAttribute("content", ogImage);
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else if (prevContent !== null) meta.setAttribute("content", prevContent);
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#101010] text-[#F5F5F0] flex items-center justify-center text-xs uppercase tracking-widest text-[#555550]" data-testid="work-detail-loading-state">
        Loading…
      </div>
    );
  }

  if (isError || !data?.post) {
    return (
      <div className="min-h-screen bg-[#101010] text-[#F5F5F0]" data-testid={WORK_DETAIL.notFound}>
        <Nav />
        <div className="flex flex-col items-center justify-center gap-4 py-32">
          <p className="text-sm text-[#888880]">This project could not be found.</p>
          <Link to="/" data-testid="work-detail-not-found-back" className="text-sm text-[#CCFF00] hover:underline">
            Back to Work
          </Link>
        </div>
      </div>
    );
  }

  const { post, next } = data;

  return (
    <div className="min-h-screen bg-[#101010] text-[#F5F5F0]" data-testid={WORK_DETAIL.page}>
      <Nav />
      <div className="max-w-3xl mx-auto px-3 sm:px-6 pt-3 sm:pt-4 flex items-center justify-between">
        <Link
          to={backHref}
          data-testid={WORK_DETAIL.backButton}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#888880] hover:text-[#CCFF00] transition-colors min-h-[44px]"
        >
          <ArrowLeft size={14} /> Back to Work
        </Link>
        {next && (
          <Link
            to={`/film/${next.shortcode}`}
            data-testid={WORK_DETAIL.nextButton}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#888880] hover:text-[#CCFF00] transition-colors min-h-[44px]"
          >
            Next: {next.brandName || "Unverified brand"} <ArrowRight size={14} />
          </Link>
        )}
      </div>

      <div className="max-w-md mx-auto px-3 sm:px-6 mt-3 sm:mt-4">
        <InstagramEmbed postUrl={post.postUrl} shortcode={post.shortcode} />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
        <div className="flex items-center gap-3 flex-wrap mb-2">
          <h1 data-testid={WORK_DETAIL.brandTitle} className="text-xl sm:text-2xl tracking-tight text-[#F5F5F0]">
            {post.brandName || "Unverified brand"}
          </h1>
          {post.isAIVideo && (
            <span data-testid={WORK_DETAIL.aiBadge} className="text-[10px] uppercase tracking-widest bg-[#181818] text-[#CCFF00] px-2 py-1 border border-[#CCFF00]/40">
              AI Film
            </span>
          )}
          {post.coverStatus === "unavailable" && (
            <span data-testid={WORK_DETAIL.coverPendingBadge} className="text-[10px] uppercase tracking-widest bg-[#181818] text-[#888880] px-2 py-1 border border-[#2A2A2A]">
              No Local Cover
            </span>
          )}
        </div>
        <p data-testid={WORK_DETAIL.category} className="text-xs sm:text-sm uppercase tracking-widest text-[#888880]">
          {post.brandCategory || "Category unverified"}
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mt-6 pt-5 border-t border-[#2A2A2A] text-[11px] uppercase tracking-widest">
          <div>
            <p className="text-[#555550] mb-1">Year</p>
            <p data-testid="work-detail-year" className="text-[#F5F5F0]">
              {post.dateIst ? new Date(post.dateIst).getFullYear() : "Unknown"}
            </p>
          </div>
          <div>
            <p className="text-[#555550] mb-1">Account</p>
            <p data-testid="work-detail-account" className="text-[#F5F5F0]">{post.accounts?.[0] ? `@${post.accounts[0]}` : "Unknown"}</p>
          </div>
          <div>
            <p className="text-[#555550] mb-1">Production</p>
            <p data-testid="work-detail-production" className="text-[#F5F5F0]">{post.productionLabel || "Unconfirmed"}</p>
          </div>
          <div>
            <p className="text-[#555550] mb-1">Cover</p>
            <p data-testid="work-detail-cover-status" className="text-[#F5F5F0]">
              {post.coverStatus === "ready" ? "Ready" : post.coverStatus === "unavailable" ? "Unavailable" : "Pending"}
            </p>
          </div>
        </div>

        {post.videoAbout && (
          <div className="mt-6">
            <p className="text-[11px] uppercase tracking-widest text-[#555550] mb-1">About this film</p>
            <p className="text-[10px] text-[#555550] mb-2 italic">Sourced from EiPi's imported production/caption review notes — not an AI-generated visual analysis.</p>
            <p data-testid={WORK_DETAIL.videoAbout} className="text-sm sm:text-base leading-relaxed text-[#c8c8c0] max-w-2xl">
              {post.videoAbout}
            </p>
          </div>
        )}

        {post.captionWritten && (
          <div className="mt-6">
            <p className="text-[11px] uppercase tracking-widest text-[#555550] mb-1">Original caption</p>
            <p className="text-[10px] text-[#555550] mb-2 italic">Verbatim from the original Instagram post.</p>
            <p data-testid={WORK_DETAIL.caption} className="text-sm leading-relaxed text-[#888880] whitespace-pre-line max-w-2xl">
              {post.captionWritten}
            </p>
          </div>
        )}

        {post.keywords?.length > 0 && (
          <div data-testid={WORK_DETAIL.keywords} className="mt-6 flex flex-wrap gap-2">
            {post.keywords.map((k) => (
              <span key={k} className="text-[11px] uppercase tracking-widest text-[#555550]">
                #{k}
              </span>
            ))}
          </div>
        )}

        <a
          href={post.postUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={WORK_DETAIL.externalLink}
          className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#888880] hover:text-[#CCFF00] transition-colors min-h-[44px]"
        >
          View original post on Instagram <ExternalLink size={12} />
        </a>
      </div>
    </div>
  );
}
